/**
 * @fileoverview Out of recording minutes: a dialog that points at Plan &
 * usage (iOS review B6).
 *
 * The recorder sells nothing. When the minutes are gone it names the month
 * and the reset date and sends the instructor to Settings, where the plans
 * and the top-up live, or to typed notes, which never wait. An
 * organization's exhausted pool is the admin's to refill, so that version
 * says so and offers no page. The web twin of
 * `mobile/src/components/OutOfMinutesSheet.js`.
 */
import { Clock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { recordingUsageLine } from "@/lib/recordingUsage";
import { accountTier, tierDisplayName } from "@/lib/tiers";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {Object|null} props.usage - GET /subscription/usage.
 * @param {Object|null} props.user - The signed-in instructor.
 * @param {{contextName?: string|null}|null} [props.orgPool] - Set when an
 *   organization's pool refused the upload; the dialog then names it.
 * @param {boolean} [props.saved] - True when a take is already saved in
 *   this browser, so the dialog can say where it went.
 * @param {() => void} props.onPlan - Open Settings, where Plan & usage lives.
 * @param {() => void} props.onWriteNotes
 */
export function OutOfMinutesDialog({ open, onOpenChange, usage, user, orgPool = null, saved = false, onPlan, onWriteNotes }) {
  const now = new Date();
  const line = recordingUsageLine(usage, now);
  const month = MONTHS[now.getMonth()];
  const monthlyLimit = usage?.contexts?.find?.((ctx) => ctx.context === "personal")?.minutes_limit ?? usage?.minutes_limit;
  const org = orgPool ? orgPool.contextName || "Your organization" : null;

  let body;
  if (org) {
    body = `${org} has no recording minutes left this month. Ask your organization admin to add more.`;
  } else if (line?.monthly && Number(monthlyLimit) > 0) {
    body = `You’ve used all ${Math.round(Number(monthlyLimit))} minutes for ${month}. They reset on ${line.resetsOn}. Plan & usage shows what’s left and the ways to add more.`;
  } else {
    body = "You’ve used all of your recording minutes. Plan & usage shows the ways to add more.";
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--studio-overlay)] border-[var(--studio-divider-strong)] text-[var(--text-primary)]"
        data-testid="out-of-minutes"
      >
        <DialogHeader className="text-left">
          <div className="flex items-center gap-3.5">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--accent-yellow-faint)] text-[var(--accent-yellow-text)]">
              <Clock className="h-6 w-6" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <DialogTitle className="font-serif text-xl font-normal leading-tight text-[var(--text-primary)]">Out of recording minutes</DialogTitle>
              <DialogDescription className="mt-0.5 text-[13px] text-[var(--text-tertiary)]">
                {org ? org : `${month} · ${tierDisplayName(accountTier(user))}`}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <p className="text-[15px] leading-[22px] text-[var(--text-secondary)]">{body}</p>
        {saved && (
          <p className="text-[15px] leading-[22px] text-[var(--text-secondary)]">
            Your recording is saved in this browser and waits in Saved recordings until then.
          </p>
        )}
        <div className="flex flex-col gap-2.5 pt-1">
          {!org && (
            <Button
              onClick={onPlan}
              className="h-11 w-full rounded-full bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium"
              data-testid="out-of-minutes-plan"
            >
              Plan &amp; usage
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={onWriteNotes}
            className={
              org
                ? "h-11 w-full rounded-full bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium"
                : "h-11 w-full rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)] hover:bg-[var(--studio-interactive)]"
            }
            data-testid="out-of-minutes-notes"
          >
            Write notes instead
          </Button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="py-2 text-[15px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            data-testid="out-of-minutes-close"
          >
            Not now
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OutOfMinutesDialog;
